import {
  Button,
  Checkbox,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  InputGroup,
  Row,
  useOverlay,
} from '@fs/zion-ui'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../hooks/useNotifications'
import { createReservations } from '../../../services/reservationsService'
import { getUser } from '../../../services/userService'
import { PermissionsContext } from '../../app/PermissionsContext'
import { DotSeparator } from '../../metadata/MetaData'
import { ParentsSelect, SpouseSelect } from '../ordinances/printCards/PrintCardsButton'
import { PersonContext } from '../PersonContext'
import UserPicker from '../../common/UserPicker'

export default function CreateReservationsDialog() {
  const { error } = useNotifications()
  const { isAuthorized } = useContext(PermissionsContext)
  const { currentPerson, fetchCurrentPerson } = useContext(PersonContext)
  const name = currentPerson.name
  const personId = currentPerson.id
  const lifespan = currentPerson.lifespan

  const createDialogOpener = useOverlay()

  const [baptism, setBaptism] = useState(false)
  const [confirmation, setConfirmation] = useState(false)
  const [initiatory, setInitiatory] = useState(false)
  const [endowment, setEndowment] = useState(false)
  const [sealingToParents, setSealingToParents] = useState(false)
  const [sealingToSpouse, setSealingToSpouse] = useState(false)

  const [selectedItemParents, setSelectedItemParents] = useState({ key: 'selectedItemParents', primaryText: '' }) // It has parent1 and parent2
  const [selectedItemSpouse, setSelectedItemSpouse] = useState({ key: 'selectedItemParents', primaryText: '' })

  const [ownerSearchValue, setOwnerSearchValue] = useState('')
  const [ownerSearchType, setOwnerSearchType] = useState('username')
  const [ownerId, setOwnerId] = useState('')
  const debounceTimeout = useRef()

  const { t } = useTranslation()

  const handleUserSelected = (selectedUser) => {
    if (selectedUser) {
      setOwnerId(selectedUser.contributorId)
    }
  }

  const lookUpUser = useCallback(
    (userNameOrId) => {
      getUser(userNameOrId, 'contributorId')
        .then((usersResponse) => {
          let user
          if (usersResponse.data instanceof Array && usersResponse.data.length > 0) {
            user = usersResponse.data[0]
          } else {
            user = usersResponse.data
          }
          handleUserSelected(user)
        })
        .catch((err) => {
          error(
            t('user.errors.gettingUser', {
              defaultValue: `Error getting user: ${err?.response?.data?.notifications?.[0]?.msg || err.message || ''}`,
              message: err?.response?.data?.notifications?.[0]?.msg || err.message || '',
            }),
            err
          )
        })
    },
    [error, t]
  )

  useEffect(() => {
    setOwnerId('')
    clearTimeout(debounceTimeout.current)
    if (ownerSearchValue && ownerSearchValue.length > 0) {
      debounceTimeout.current = setTimeout(() => lookUpUser(ownerSearchValue), 700)
    }
  }, [lookUpUser, ownerSearchType, ownerSearchValue])

  const handleCreateReservations = useCallback(() => {
    const ordinanceTypes = []
    const personIds = {
      individualId: personId,
    }
    if (baptism) {
      ordinanceTypes.push('BAPTISM')
    }
    if (confirmation) {
      ordinanceTypes.push('CONFIRMATION')
    }
    if (initiatory) {
      ordinanceTypes.push('INITIATORY')
    }
    if (endowment) {
      ordinanceTypes.push('ENDOWMENT')
    }
    if (sealingToParents) {
      ordinanceTypes.push('SEALING_TO_PARENTS')
      personIds.childId = personId
      personIds.parent1Id = selectedItemParents.relationship.parent1.personId
      personIds.parent2Id = selectedItemParents.relationship.parent2.personId
    }
    if (sealingToSpouse) {
      ordinanceTypes.push('SEALING_TO_SPOUSE')
      personIds.spouse1Id = personId
      personIds.spouse2Id = selectedItemSpouse.relationship.spouse.personId
    }

    const request = {
      ownerId,
      ordinanceTypes,
      personIds,
    }

    createReservations(request)
      .then(() => {
        reset()
        fetchCurrentPerson()
      })
      .catch((err) => {
        error(
          t('reservation.errors.creatingReservations', {
            defaultValue: `Error creating reservations: ${err.message || ''}`,
            message: err.message || '',
          }),
          err
        )
      })
  }, [
    baptism,
    confirmation,
    endowment,
    error,
    fetchCurrentPerson,
    initiatory,
    ownerId,
    personId,
    sealingToParents,
    sealingToSpouse,
    selectedItemParents,
    selectedItemSpouse,
    t,
  ])

  if (!currentPerson) {
    return null
  }

  function reset() {
    setBaptism(false)
    setConfirmation(false)
    setInitiatory(false)
    setEndowment(false)
    setSealingToParents(false)
    setSealingToSpouse(false)
    setOwnerSearchValue('')
    setOwnerSearchType('username')
    setOwnerId('')
  }

  function enableCreateButton() {
    if (!ownerId) {
      return false
    }
    if (sealingToParents && selectedItemParents?.relationship) {
      return selectedItemParents.relationship.parent1?.personId && selectedItemParents.relationship.parent2?.personId
    }
    if (sealingToSpouse && selectedItemSpouse?.relationship) {
      return true
    }
    return baptism || confirmation || initiatory || endowment
  }

  return (
    <div>
      <Button
        id="PersonReservationsCreateButton"
        variant="contained"
        onClick={() => createDialogOpener.open()}
        disabled={!isAuthorized('ReserveOrdinancePermission')}
        emphasis="high"
      >
        {t('action.create', 'Create')}
      </Button>
      <DialogOverlay {...createDialogOpener} title={t('reservations.create', 'Create Reservations')}>
        <Row gutters="xxs">
          {name}
          <DotSeparator />
          {personId}
          <DotSeparator />
          {lifespan}
        </Row>
        <div>
          <Checkbox
            name="Baptism"
            label={t('ordinances.types.baptism', 'Baptism')}
            checked={baptism}
            onChange={(event) => setBaptism(event.target.checked)}
          />
        </div>
        <div>
          <Checkbox
            name="Confirmation"
            label={t('ordinances.types.confirmation', 'Confirmation')}
            checked={confirmation}
            onChange={(event) => setConfirmation(event.target.checked)}
          />
        </div>
        <div>
          <Checkbox
            name="Initiatory"
            label={t('ordinances.types.initiatory', 'Initiatory')}
            checked={initiatory}
            onChange={(event) => setInitiatory(event.target.checked)}
          />
        </div>
        <div>
          <Checkbox
            name="Endowment"
            label={t('ordinances.types.endowment', 'Endowment')}
            checked={endowment}
            onChange={(event) => setEndowment(event.target.checked)}
          />
        </div>
        <div>
          <Checkbox
            name="Sealing to Parents"
            label={t('ordinances.types.sealingToParents', 'Sealing to Parents')}
            checked={sealingToParents}
            onChange={(event) => setSealingToParents(event.target.checked)}
            disabled={!currentPerson.relationships?.parents || currentPerson.relationships?.parents.length < 1}
          />
        </div>
        {sealingToParents && currentPerson.relationships?.parents && (
          <ParentsSelect
            value={selectedItemParents}
            onChange={setSelectedItemParents}
            relationshipList={currentPerson.relationships?.parents}
          />
        )}
        <div>
          <Checkbox
            name="Sealing to Spouse"
            label={t('ordinances.types.sealingToSpouse', 'Sealing to Spouse')}
            checked={sealingToSpouse}
            onChange={(event) => setSealingToSpouse(event.target.checked)}
            disabled={!currentPerson.relationships?.spouses || currentPerson.relationships?.spouses.length < 1}
          />
        </div>
        {sealingToSpouse && currentPerson.relationships?.spouses && (
          <SpouseSelect
            value={selectedItemSpouse}
            onChange={setSelectedItemSpouse}
            relationshipList={currentPerson.relationships?.spouses}
          />
        )}
        <InputGroup label={t('reservations.owner', 'Person to receive reservation')}>
          <Row>
            <UserPicker
              aria-label="ownerTextField"
              onUserChanged={handleUserSelected}
              id="usernameInput"
              label="Username or User ID"
              userName={ownerSearchValue}
            />
          </Row>
        </InputGroup>

        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button
              id="Dialog Create Button"
              emphasis="high"
              disabled={!enableCreateButton()}
              onClick={handleCreateReservations}
            >
              {t('action.create', 'Create')}
            </Button>
            <Button
              id="Dialog Cancel Button"
              emphasis="medium"
              onClick={() => {
                reset()
                createDialogOpener.close()
              }}
            >
              {t('action.cancel', 'Cancel')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </div>
  )
}
